/**
 * useCSVImport Hook
 *
 * Manages CSV import execution state and provides import functions.
 * Handles loading states, error handling, and success/error callbacks.
 *
 * @module ImportExportTab/hooks/useCSVImport
 */

import { useState, useCallback } from 'react';
import { importOrgUnits, importLocations } from '../services/bulkImportService';

/**
 * Custom hook for managing CSV bulk import execution
 *
 * Provides state management and execution logic for importing
 * validated CSV records into the database.
 *
 * @param {Object[]} existingOrgUnits - Existing OrgUnits for ID resolution
 * @returns {Object} Import state and functions
 * @returns {Object} return.importState - Current import state
 * @returns {boolean} return.importState.loading - Import in progress
 * @returns {string|null} return.importState.error - Error message if import failed
 * @returns {Object|null} return.importState.results - Import results (success/errors/summary)
 * @returns {Function} return.executeImport - Function to execute import
 * @returns {Function} return.resetImport - Function to reset import state
 *
 * @example
 * const { importState, executeImport, resetImport } = useCSVImport(existingOrgUnits);
 *
 * const handleImport = async () => {
 *   await executeImport('orgUnits', validationResult, clientId, user, {
 *     onSuccess: (results) => console.log('Imported:', results.summary.successCount),
 *     onError: (error) => console.error('Failed:', error)
 *   });
 * };
 */
export default function useCSVImport(existingOrgUnits = []) {
  const [importState, setImportState] = useState({
    loading: false,
    error: null,
    results: null
  });

  /**
   * Execute bulk import
   *
   * @param {string} importType - Type of import ("orgUnits" or "locations")
   * @param {Object} validationResult - Validation result with validRecords array
   * @param {string} clientId - Client ID for multi-tenancy
   * @param {Object} user - Current user object
   * @param {Object} [options] - Optional configuration
   * @param {Function} [options.onSuccess] - Callback on successful import
   * @param {Function} [options.onError] - Callback on import error
   * @returns {Promise<void>}
   */
  const executeImport = useCallback(async (
    importType,
    validationResult,
    clientId,
    user,
    options = {}
  ) => {
    const { onSuccess, onError } = options;

    // Set loading state
    setImportState({
      loading: true,
      error: null,
      results: null
    });

    try {
      // Execute import with existingOrgUnits for ID resolution
      let results;
      if (importType === 'orgUnits') {
        results = await importOrgUnits(
          validationResult.validRecords,
          clientId,
          user,
          existingOrgUnits
        );
      } else {
        results = await importLocations(
          validationResult.validRecords,
          clientId,
          user,
          existingOrgUnits
        );
      }

      // Update state with results
      setImportState({
        loading: false,
        error: null,
        results
      });

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(results);
      }
    } catch (error) {
      // Update state with error
      const errorMessage = error?.message || String(error);
      setImportState({
        loading: false,
        error: errorMessage,
        results: null
      });

      // Call error callback if provided
      if (onError) {
        onError(error);
      }
    }
  }, [existingOrgUnits]);

  /**
   * Reset import state
   *
   * Clears loading, error, and results state.
   * Use this to clear state before a new import or to dismiss results.
   */
  const resetImport = useCallback(() => {
    setImportState({
      loading: false,
      error: null,
      results: null
    });
  }, []);

  return {
    importState,
    executeImport,
    resetImport
  };
}
